// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseInputEmailMultiple, BaseTextEditor, FileUpload, Loading } from '~/components/common';
// Others
import { sendEmail } from '~/thunks/contact/contactThunk';
import { defaultValuesForm, schema } from './helper';
import { ModeTextEditorEnum } from '~/utils/enum/common';
import { IBodySendMail, IFormSendMail } from '~/utils/interface/contact';
import { ACCEPT_ALL_FILES } from '~/utils/constants/common';
import { createFormData } from '~/utils/helper/common';
import { mockEmailForm } from '~/utils/mockData/contact';
import { useAppDispatch } from '~/utils/redux/hooks';
// Styles, images, icons
import styles from './FormSendEmailModal.module.scss';

type Props<T> = {
  dataDefault?: T | null;
  isOpen: boolean;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const FormSendEmailModal = <T extends { id: string; email?: string }>(props: Props<T>) => {
  //#region Destructuring Props
  const { dataDefault, isOpen, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/contact', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormSendMail>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('from', mockEmailForm);
  }, [setValue]);

  // useEffect(() => {
  //   if (!profile?.email) return;

  //   setValue('from', profile?.email);

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [profile?.email]);

  useEffect(() => {
    if (!dataDefault || !dataDefault?.email) return;

    setValue('to', [dataDefault?.email]);
  }, [dataDefault, setValue]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSendEmail = async (data: IFormSendMail) => {
    if (!dataDefault || !dataDefault?.id) return;

    const mockData: IFormSendMail = { ...data, from: mockEmailForm };

    const formData = await createFormData(mockData, 'files');

    const payload: IBodySendMail = {
      id: dataDefault?.id,
      body: formData,
    };

    setIsLoading(true);

    dispatch(sendEmail(payload))
      .unwrap()
      .then((_res) => {
        handleCloseFormSendMail();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseFormSendMail = () => {
    onClose();
    reset(defaultValuesForm);
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='formSendEmailModalComponent'
      open={isOpen}
      fullWidth
      maxWidth={'md'}
      onClose={handleCloseFormSendMail}
    >
      <Box component={'form'} onSubmit={handleSubmit(handleSendEmail)} className={cx('form')}>
        <DialogTitle>{t('form_send_mail_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container size={12} rowSpacing={'24px'} columnSpacing={'12px'}>
            <Grid2 size={12}>
              <Controller
                name='from'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    value={value}
                    label={t('form_send_mail_from_label')}
                    onChange={onChange}
                    required
                    errorMessage={errors.from?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name='to'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInputEmailMultiple
                    value={value}
                    label={t('form_send_mail_to_label')}
                    onChange={onChange}
                    required
                    errorMessage={errors.to?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name='cc'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInputEmailMultiple
                    value={value}
                    label={t('form_send_mail_cc_label')}
                    onChange={onChange}
                    errorMessage={errors.cc?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={4}>
              <Controller
                name='bcc'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInputEmailMultiple
                    value={value}
                    label={t('form_send_mail_bcc_label')}
                    onChange={onChange}
                    errorMessage={errors.bcc?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='subject'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    value={value}
                    label={t('form_send_mail_subject_label')}
                    onChange={onChange}
                    required
                    errorMessage={errors.subject?.message}
                  />
                )}
              />
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='content'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseTextEditor
                    mode={ModeTextEditorEnum.CREATE}
                    height={200}
                    label={t('form_send_mail_content_label')}
                    value={value}
                    onChange={(textEditor: string) => onChange(textEditor)}
                    required
                    errorMessage={errors.content?.message}
                  />
                )}
              />
            </Grid2>

            <Controller
              name='files'
              control={control}
              render={({ field: { onChange } }) => (
                <FileUpload
                  multiple
                  accept={ACCEPT_ALL_FILES}
                  onChange={onChange}
                  errorMessage={errors.files?.message}
                />
              )}
            />
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCloseFormSendMail}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit'>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </DialogActions>

        {isLoading && <Loading />}
      </Box>
    </Dialog>
  );
};

export default FormSendEmailModal;
